import type { AppLocale } from '../i18n/i18n';
import { readPreference, writePreference } from './preferences';

export const calendarSystems = ['gregorian', 'persian', 'islamic'] as const;
export type CalendarSystem = (typeof calendarSystems)[number];

export const digitSystems = ['latin', 'persian', 'arabic-indic'] as const;

export const iranCurrencyDisplayModes = ['rial', 'toman', 'both'] as const;
export type IranCurrencyDisplayMode = (typeof iranCurrencyDisplayModes)[number];
export type DigitSystem = (typeof digitSystems)[number];

export interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

export const DISPLAY_CALENDAR_PREFERENCE_KEY = 'varza.displayCalendar';
export const DIGIT_SYSTEM_PREFERENCE_KEY = 'varza.digitSystem';
export const IRAN_CURRENCY_DISPLAY_PREFERENCE_KEY = 'varza.iranCurrencyDisplay';
export const DISPLAY_PREFERENCES_EVENT = 'varza:display-preferences';

const MS_PER_DAY = 86_400_000;
const referenceSundayUtc = Date.UTC(2021, 0, 3);

const calendarIdentifiers: Readonly<Record<CalendarSystem, string>> = {
  gregorian: 'gregory',
  persian: 'persian',
  // Arabic (Saudi Arabia) uses Umm al-Qura. Keeping that explicit makes typed input and display
  // use the same calendar instead of silently mixing a tabular converter with a regional grid.
  islamic: 'islamic-umalqura',
};

const numberingSystems: Readonly<Record<DigitSystem, string>> = {
  latin: 'latn',
  persian: 'arabext',
  'arabic-indic': 'arab',
};

const weekStartByCalendar: Readonly<Record<CalendarSystem, number>> = {
  gregorian: 0,
  persian: 6,
  islamic: 6,
};

const weekendByCalendar: Readonly<Record<CalendarSystem, readonly number[]>> = {
  gregorian: [0, 6],
  persian: [5],
  islamic: [5, 6],
};

function isCalendarSystem(value: string): value is CalendarSystem {
  return (calendarSystems as readonly string[]).includes(value);
}

function isDigitSystem(value: string): value is DigitSystem {
  return (digitSystems as readonly string[]).includes(value);
}

function isIranCurrencyDisplayMode(value: string): value is IranCurrencyDisplayMode {
  return (iranCurrencyDisplayModes as readonly string[]).includes(value);
}

function publishDisplayPreferences(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(DISPLAY_PREFERENCES_EVENT));
}

/** Locale is used only to choose a first value. The stored calendar remains independent later. */
export function getCalendarForLocale(locale: AppLocale): CalendarSystem {
  if (locale === 'fa') return 'persian';
  if (locale === 'ar') return 'islamic';
  return 'gregorian';
}

export function getDigitSystemForLocale(locale: AppLocale): DigitSystem {
  if (locale === 'fa') return 'persian';
  if (locale === 'ar') return 'arabic-indic';
  return 'latin';
}

export function getDisplayCalendarPreference(locale: AppLocale): CalendarSystem {
  const stored = readPreference(DISPLAY_CALENDAR_PREFERENCE_KEY);
  if (isCalendarSystem(stored)) return stored;
  const initial = getCalendarForLocale(locale);
  writePreference(DISPLAY_CALENDAR_PREFERENCE_KEY, initial);
  return initial;
}

export function setDisplayCalendarPreference(calendar: CalendarSystem): void {
  writePreference(DISPLAY_CALENDAR_PREFERENCE_KEY, calendar);
  publishDisplayPreferences();
}

export function getDigitSystemPreference(locale: AppLocale): DigitSystem {
  const stored = readPreference(DIGIT_SYSTEM_PREFERENCE_KEY);
  if (isDigitSystem(stored)) return stored;
  const initial = getDigitSystemForLocale(locale);
  writePreference(DIGIT_SYSTEM_PREFERENCE_KEY, initial);
  return initial;
}

export function setDigitSystemPreference(digits: DigitSystem): void {
  writePreference(DIGIT_SYSTEM_PREFERENCE_KEY, digits);
  publishDisplayPreferences();
}

/**
 * Iran-currency display is a user choice only. It never changes the tenant's accounting base,
 * stored denomination, or exchange-rate evidence.
 */
export function getIranCurrencyDisplayPreference(): IranCurrencyDisplayMode {
  const stored = readPreference(IRAN_CURRENCY_DISPLAY_PREFERENCE_KEY);
  return isIranCurrencyDisplayMode(stored) ? stored : 'rial';
}

export function setIranCurrencyDisplayPreference(mode: IranCurrencyDisplayMode): void {
  writePreference(IRAN_CURRENCY_DISPLAY_PREFERENCE_KEY, mode);
  publishDisplayPreferences();
}

export function localeTag(locale: AppLocale): string {
  if (locale === 'fa') return 'fa-IR';
  if (locale === 'ar') return 'ar-SA';

  return 'en-US';
}

export function calendarLocaleTag(
  locale: AppLocale,
  calendar = getDisplayCalendarPreference(locale),
  digits = getDigitSystemPreference(locale),
): string {
  return `${localeTag(locale)}-u-ca-${calendarIdentifiers[calendar]}-nu-${numberingSystems[digits]}`;
}

function utcTimestamp(year: number, month: number, day: number): number {
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(year, month - 1, day);
  return date.getTime();
}

function isoOf(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

export function parseIsoDate(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/u.exec(value);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = utcTimestamp(year, month, day);
  return isoOf(timestamp) === `${match[1]}-${match[2]}-${match[3]}` ? timestamp : null;
}

const partsFormatterByCalendar: Readonly<Record<'persian' | 'islamic', Intl.DateTimeFormat>> = {
  persian: new Intl.DateTimeFormat('en-US-u-ca-persian-nu-latn', {
    timeZone: 'UTC', year: 'numeric', month: 'numeric', day: 'numeric',
  }),
  islamic: new Intl.DateTimeFormat('en-US-u-ca-islamic-umalqura-nu-latn', {
    timeZone: 'UTC', year: 'numeric', month: 'numeric', day: 'numeric',
  }),
};

function partsOf(formatter: Intl.DateTimeFormat, timestamp: number): CalendarDate {
  const parts = formatter.formatToParts(new Date(timestamp));
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number.parseInt(parts.find((part) => part.type === type)?.value ?? '', 10);
  return { year: read('year'), month: read('month'), day: read('day') };
}

function compareCalendarDates(left: CalendarDate, right: CalendarDate): number {
  if (left.year !== right.year) return left.year - right.year;
  if (left.month !== right.month) return left.month - right.month;
  return left.day - right.day;
}

function isoToIntlCalendar(iso: string, calendar: 'persian' | 'islamic'): CalendarDate | null {
  const timestamp = parseIsoDate(iso);
  return timestamp === null ? null : partsOf(partsFormatterByCalendar[calendar], timestamp);
}

/**
 * Inverts an Intl calendar without approximating its month rules. Calendar dates increase
 * monotonically one UTC day at a time, so a binary search finds the one canonical ISO day and
 * naturally rejects impossible dates such as the thirtieth day of a 29-day Hijri month.
 */
function intlCalendarToIso(date: CalendarDate, calendar: 'persian' | 'islamic'): string | null {
  const { year, month, day } = date;
  if (![year, month, day].every(Number.isInteger)) return null;
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) return null;

  const roughGregorianYear = year + (calendar === 'persian' ? 621 : 579);
  let low = Math.floor(utcTimestamp(roughGregorianYear - 2, 1, 1) / MS_PER_DAY);
  let high = Math.floor(utcTimestamp(roughGregorianYear + 2, 12, 31) / MS_PER_DAY);

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const current = partsOf(partsFormatterByCalendar[calendar], middle * MS_PER_DAY);
    if (compareCalendarDates(current, date) < 0) low = middle + 1;
    else high = middle - 1;
  }

  const candidate = low * MS_PER_DAY;
  return compareCalendarDates(partsOf(partsFormatterByCalendar[calendar], candidate), date) === 0
    ? isoOf(candidate)
    : null;
}

export function isoToPersian(iso: string): CalendarDate | null {
  return isoToIntlCalendar(iso, 'persian');
}

export function persianToIso(date: CalendarDate): string | null {
  return intlCalendarToIso(date, 'persian');
}

export function isoToIslamic(iso: string): CalendarDate | null {
  return isoToIntlCalendar(iso, 'islamic');
}

export function islamicToIso(date: CalendarDate): string | null {
  return intlCalendarToIso(date, 'islamic');
}

export function isoToCalendar(iso: string, calendar: CalendarSystem): CalendarDate | null {
  if (calendar === 'persian') return isoToPersian(iso);
  if (calendar === 'islamic') return isoToIslamic(iso);
  const timestamp = parseIsoDate(iso);
  if (timestamp === null) return null;
  const date = new Date(timestamp);
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

export function calendarToIso(date: CalendarDate, calendar: CalendarSystem): string | null {
  if (calendar === 'persian') return persianToIso(date);
  if (calendar === 'islamic') return islamicToIso(date);
  const { year, month, day } = date;
  if (![year, month, day].every(Number.isInteger)) return null;
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const timestamp = utcTimestamp(year, month, day);
  const candidate = new Date(timestamp);
  if (candidate.getUTCFullYear() !== year || candidate.getUTCMonth() !== month - 1 || candidate.getUTCDate() !== day) return null;
  return isoOf(timestamp);
}

const arabicIndicDigits = '٠١٢٣٤٥٦٧٨٩';
const persianDigits = '۰۱۲۳۴۵۶۷۸۹';

export function toLatinDigits(value: string): string {
  return value.replace(/[۰-۹٠-٩]/gu, (digit) => {
    const persianIndex = persianDigits.indexOf(digit);
    return String(persianIndex >= 0 ? persianIndex : arabicIndicDigits.indexOf(digit));
  });
}

/** Normalizes localized numeric text for parsing and API payloads without changing its value. */
export function normalizeLocalizedNumberInput(value: string): string {
  return toLatinDigits(value)
    .replace(/[٬،,\s\u00a0]/gu, '')
    .replace(/[٫]/gu, '.')
    .replace(/[−–—]/gu, '-');
}

export function toLocaleDigits(
  value: string,
  locale: AppLocale,
  digits = getDigitSystemPreference(locale),
): string {
  const latin = toLatinDigits(value);
  if (digits === 'latin') return latin;
  const target = digits === 'persian' ? persianDigits : arabicIndicDigits;
  return latin.replace(/[0-9]/gu, (digit) => target[Number(digit)] ?? digit);
}

/** Formats an ISO date as the `yyyy/mm/dd` pattern used by Varza date fields. */
export function isoToCalendarInput(
  iso: string,
  calendar: CalendarSystem,
  locale: AppLocale,
  digits = getDigitSystemPreference(locale),
): string {
  const parts = isoToCalendar(iso, calendar);
  if (parts === null) return '';
  const pattern = `${String(parts.year).padStart(4, '0')}/${String(parts.month).padStart(2, '0')}/${String(parts.day).padStart(2, '0')}`;
  return toLocaleDigits(pattern, locale, digits);
}

export function calendarInputToIso(input: string, calendar: CalendarSystem): string | null {
  const segments = toLatinDigits(input).trim().split(/[/\-.]/u);
  if (segments.length !== 3 || segments.some((segment) => !/^\d+$/u.test(segment.trim()))) return null;
  const [year, month, day] = segments.map((segment) => Number.parseInt(segment, 10));
  if (year === undefined || month === undefined || day === undefined) return null;
  return calendarToIso({ year, month, day }, calendar);
}

export function calendarInputPlaceholder(
  calendar: CalendarSystem,
  locale: AppLocale,
  digits = getDigitSystemPreference(locale),
): string {
  const sample = calendar === 'persian' ? '1405/01/01' : calendar === 'islamic' ? '1448/01/01' : '2026/01/01';
  return toLocaleDigits(sample, locale, digits);
}

function formatter(
  calendar: CalendarSystem,
  locale: AppLocale,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${calendarLocaleTag(locale, calendar)}|${JSON.stringify(options)}`;
  let cached = formatterCache.get(key);
  if (cached === undefined) {
    cached = new Intl.DateTimeFormat(calendarLocaleTag(locale, calendar), { timeZone: 'UTC', ...options });
    formatterCache.set(key, cached);
  }
  return cached;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

/** Today in the browser's own time zone, so late-evening users do not see yesterday. */
export function todayIso(): string {
  const now = new Date();
  return isoOf(utcTimestamp(now.getFullYear(), now.getMonth() + 1, now.getDate()));
}

export function shiftIsoDays(iso: string, days: number): string | null {
  const timestamp = parseIsoDate(iso);
  return timestamp === null || !Number.isInteger(days) ? null : isoOf(timestamp + days * MS_PER_DAY);
}

/** Day of week as 0 (Sunday) through 6 (Saturday). */
export function isoWeekday(iso: string): number {
  const timestamp = parseIsoDate(iso);
  return timestamp === null ? 0 : new Date(timestamp).getUTCDay();
}

export function calendarWeekStart(calendar: CalendarSystem): number {
  return weekStartByCalendar[calendar];
}

/** Column of a date inside a week row that begins on the calendar's own first weekday. */
export function calendarWeekColumn(iso: string, calendar: CalendarSystem): number {
  return (isoWeekday(iso) - weekStartByCalendar[calendar] + 7) % 7;
}

export function isWeekendIso(iso: string, calendar: CalendarSystem): boolean {
  return weekendByCalendar[calendar].includes(isoWeekday(iso));
}

function isGregorianLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

const gregorianMonthLengths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export function calendarMonthLength(year: number, month: number, calendar: CalendarSystem): number {
  if (!Number.isInteger(year) || year < 1 || !Number.isInteger(month) || month < 1 || month > 12) return 0;
  if (calendar === 'persian') {
    if (month <= 6) return 31;
    if (month <= 11) return 30;
    return persianToIso({ year, month: 12, day: 30 }) === null ? 29 : 30;
  }
  if (calendar === 'islamic') return islamicToIso({ year, month, day: 30 }) === null ? 29 : 30;
  if (month === 2) return isGregorianLeapYear(year) ? 29 : 28;
  return gregorianMonthLengths[month - 1] ?? 0;
}

export function addCalendarMonths(cursor: { year: number; month: number }, delta: number): { year: number; month: number } {
  const absolute = cursor.year * 12 + (cursor.month - 1) + delta;
  return { year: Math.floor(absolute / 12), month: ((absolute % 12) + 12) % 12 + 1 };
}

export function calendarMonthNames(calendar: CalendarSystem, locale: AppLocale): string[] {
  const format = formatter(calendar, locale, { month: 'long' });
  const anchorYear = calendar === 'persian' ? 1405 : calendar === 'islamic' ? 1447 : 2026;
  return Array.from({ length: 12 }, (_, index) => {
    const anchor = calendarToIso({ year: anchorYear, month: index + 1, day: 1 }, calendar);
    return anchor === null ? String(index + 1) : format.format(new Date(`${anchor}T00:00:00Z`));
  });
}

export interface WeekdayLabel {
  short: string;
  long: string;
}

export function calendarWeekdayNames(calendar: CalendarSystem, locale: AppLocale): WeekdayLabel[] {
  const shortFormat = formatter(calendar, locale, { weekday: locale === 'en' ? 'short' : 'narrow' });
  const longFormat = formatter(calendar, locale, { weekday: 'long' });
  return Array.from({ length: 7 }, (_, column) => {
    const date = new Date(referenceSundayUtc + ((weekStartByCalendar[calendar] + column) % 7) * MS_PER_DAY);
    return { short: shortFormat.format(date), long: longFormat.format(date) };
  });
}

/** Human-readable date used for cell labels and equivalent-calendar hints. */
export function formatCalendarLong(
  iso: string,
  calendar: CalendarSystem,
  locale: AppLocale,
  withWeekday = false,
): string {
  const timestamp = parseIsoDate(iso);
  if (timestamp === null) return '';
  return formatter(calendar, locale, {
    ...(withWeekday ? { weekday: 'long' as const } : {}),
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(timestamp));
}

export function persianMonthName(month: number): string {
  return calendarMonthNames('persian', 'fa')[month - 1] ?? String(month);
}
